/**
 * Phase 63 — Page-level SEO copy registry (DE/FR/IT/EN).
 *
 * Each entry returns { title, description } per locale.
 * Titles are kept ≤ 60 chars to avoid Google truncation.
 * Descriptions target 140-160 chars.
 */

const COPY = {
  home: {
    de: {
      title: "CarHive – Schweizer Auto-Marktplatz | Occasionen & Neuwagen",
      description: "Tausende Fahrzeuge aus der ganzen Schweiz – inserieren, vergleichen und kaufen ohne Kommission. Premium-Plattform für Occasionen und Neuwagen.",
    },
    fr: {
      title: "CarHive – Marketplace auto suisse | Occasions & Neufs",
      description: "Des milliers de véhicules en Suisse – publier, comparer, acheter sans commission. Plateforme premium pour occasions et véhicules neufs.",
    },
    it: {
      title: "CarHive – Marketplace auto svizzero | Usato & Nuovo",
      description: "Migliaia di veicoli in Svizzera – pubblica, confronta, acquista senza commissioni. Piattaforma premium per usato e nuovo.",
    },
    en: {
      title: "CarHive – Swiss Car Marketplace | Used & New Cars",
      description: "Thousands of cars across Switzerland – list, compare and buy without commission. Premium marketplace for used and new vehicles.",
    },
  },
  listings: {
    de: {
      title: "Auto Occasionen & Neuwagen kaufen | CarHive Schweiz",
      description: "Durchsuche tausende Auto-Inserate aus der ganzen Schweiz. Filter nach Marke, Modell, Preis, Kanton. Direkt vom Privatverkäufer oder Händler.",
    },
    fr: {
      title: "Voitures d'occasion et neuves à vendre | CarHive Suisse",
      description: "Parcourez des milliers d'annonces auto en Suisse. Filtres par marque, modèle, prix, canton. Directement de particuliers ou de garages.",
    },
    it: {
      title: "Auto usate e nuove in vendita | CarHive Svizzera",
      description: "Sfoglia migliaia di annunci auto in Svizzera. Filtri per marca, modello, prezzo, cantone. Direttamente da privati o concessionari.",
    },
    en: {
      title: "Used & New Cars for Sale in Switzerland | CarHive",
      description: "Browse thousands of car listings across Switzerland. Filter by brand, model, price, canton. Direct from private sellers or dealers.",
    },
  },
  pricing: {
    de: {
      title: "Preise & Abos | Auto inserieren auf CarHive",
      description: "Transparente Preise für Privatverkäufer, Premium-Inserate und Händler-Abos. 14 Tage gratis testen. Stripe, TWINT und Karten unterstützt.",
    },
    fr: {
      title: "Tarifs & abonnements | Publier sur CarHive",
      description: "Tarifs transparents pour particuliers, annonces premium et abonnements dealer. 14 jours gratuits. Stripe, TWINT et cartes pris en charge.",
    },
    it: {
      title: "Prezzi & abbonamenti | Pubblica su CarHive",
      description: "Prezzi trasparenti per privati, annunci premium e abbonamenti dealer. 14 giorni gratuiti. Supporto Stripe, TWINT e carte.",
    },
    en: {
      title: "Pricing & Plans | Sell Your Car on CarHive",
      description: "Transparent pricing for private sellers, premium listings and dealer plans. 14-day free trial. Stripe, TWINT and cards supported.",
    },
  },
  dealers: {
    de: {
      title: "Autohändler in der Schweiz | CarHive",
      description: "Finde geprüfte Autohändler in deiner Region. Verifizierte Profile, Bewertungen, Öffnungszeiten und alle aktiven Inserate auf einen Blick.",
    },
    fr: {
      title: "Garages auto en Suisse | CarHive",
      description: "Trouvez des garages vérifiés dans votre région. Profils certifiés, avis, horaires et toutes les annonces actives en un coup d'œil.",
    },
    it: {
      title: "Concessionari auto in Svizzera | CarHive",
      description: "Trova concessionari verificati nella tua regione. Profili certificati, recensioni, orari e tutti gli annunci attivi.",
    },
    en: {
      title: "Car Dealers in Switzerland | CarHive",
      description: "Find verified car dealers in your region. Certified profiles, reviews, opening hours and all active listings at a glance.",
    },
  },
};

/**
 * Helper: get { title, description } for a page key and locale.
 * Falls back to "de" then "en" if a locale entry is missing.
 */
export function getPageSeo(pageKey, locale = "de") {
  const page = COPY[pageKey];
  if (!page) return { title: "CarHive", description: "" };
  return page[locale] || page.de || page.en || { title: "CarHive", description: "" };
}

/**
 * Build 4-locale hreflangs object for a path. Locale-aware path can differ —
 * passing a localePathFn customises per locale (e.g. /vergleich vs /compare).
 */
export function buildHreflangs(siteUrl, path, { localePathFn } = {}) {
  const locales = ["de", "fr", "it", "en"];
  const out = {};
  for (const lc of locales) {
    const p = localePathFn ? localePathFn(lc) : path;
    out[lc] = `${siteUrl}/${lc}${p}`;
  }
  out["x-default"] = `${siteUrl}/de${localePathFn ? localePathFn("de") : path}`;
  return out;
}

/**
 * Build a generic BreadcrumbList JSON-LD.
 * @param {Array<{name:string, url:string}>} crumbs
 */
export function buildBreadcrumbList(crumbs) {
  if (!crumbs || crumbs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

/**
 * CollectionPage JSON-LD — for index pages like /listings, /dealers.
 */
export function buildCollectionPageSchema({ url, name, description, numberOfItems }) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": url,
    url,
    name,
    description,
    ...(typeof numberOfItems === "number" ? { numberOfItems } : {}),
    isPartOf: { "@type": "WebSite", name: "CarHive" },
  };
}
